/**
 * Invite landing page — /invite/:token
 * Resolves an invitation token and lets a skipper claim their boat entry.
 * On claim: creates user profile, stores JWT, redirects to regatta page.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { ResolveInviteResponse, ClaimInviteResponse } from '@regatta-replay/types';
import { formatSailNumber } from '@regatta-replay/types';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  PhoneVerificationStep,
  type PhoneVerificationResult,
} from '@/components/claim/PhoneVerificationStep';

type PageState = 'loading' | 'valid' | 'invalid' | 'claiming' | 'error';

export function InviteLanding() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invite, setInvite] = useState<ResolveInviteResponse | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Phone-verification (same as TrackClaimPage — optional, backend-enforced)
  const [phoneVerify, setPhoneVerify] = useState<PhoneVerificationResult | null>(null);

  // Resolve invite on mount
  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setErrorMessage('No invite token provided.');
      return;
    }

    let cancelled = false;

    async function resolve() {
      try {
        const data = await api.get<ResolveInviteResponse>(`/invitations/${token}`);
        if (cancelled) return;

        if (!data.valid) {
          setPageState('invalid');
          if (data.expired) {
            setErrorMessage('This invite link has expired.');
          } else if (data.alreadyClaimed) {
            setErrorMessage('This invite has already been claimed.');
          } else {
            setErrorMessage('This invite link is no longer valid.');
          }
          return;
        }

        setInvite(data);
        setDisplayName(data.skipperName || '');
        setPageState('valid');
      } catch {
        if (cancelled) return;
        setPageState('error');
        setErrorMessage('Failed to load invite. Please try again later.');
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [token]);

  async function handleClaim() {
    if (!token || !displayName.trim()) return;

    setPageState('claiming');
    try {
      const result = await api.post<ClaimInviteResponse>(
        `/invitations/${token}/claim`,
        {
          displayName: displayName.trim(),
          // Phone-verification gate fields (optional; backend-enforced when flag on)
          ...(phoneVerify?.verifyToken ? { verifyToken: phoneVerify.verifyToken } : {}),
          ...(phoneVerify?.notifyOptIn ? { notifyOptIn: true } : {}),
          ...(phoneVerify?.notifyConsentText ? { notifyConsentText: phoneVerify.notifyConsentText } : {}),
        }
      );
      login(result.token, result.user);
      navigate(`/regatta/${result.regattaSlug}`);
    } catch (err) {
      setPageState('valid');
      if (err instanceof ApiError && err.status === 400) {
        const msg = (err as ApiError & { body?: { message?: string } }).body?.message ?? '';
        if (msg.toLowerCase().includes('phone verification')) {
          setPhoneVerify(null); // Reset phone step so user can re-verify
          setErrorMessage('Phone verification is required. Please verify your phone number above.');
        } else {
          setErrorMessage(msg || 'Failed to claim invite. Please try again.');
        }
      } else {
        setErrorMessage('Failed to claim invite. Please try again.');
      }
    }
  }

  return (
    <div className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          {/* Loading */}
          {pageState === 'loading' && (
            <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm" data-testid="invite-loading">
              <Skeleton className="mx-auto mb-4 h-6 w-48" />
              <Skeleton className="mx-auto mb-2 h-5 w-64" />
              <Skeleton className="mx-auto mb-6 h-5 w-40" />
              <Skeleton className="mb-2 h-4 w-24" />
              <Skeleton className="mb-4 h-10 w-full" />
              <Skeleton className="mb-2 h-4 w-16" />
              <Skeleton className="mb-6 h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {/* Invalid / Expired / Already Claimed */}
          {(pageState === 'invalid' || pageState === 'error') && (
            <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center" data-testid="invite-error">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-navy-900">
                Invalid Invite
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {errorMessage}
              </p>
            </div>
          )}

          {/* Valid invite form */}
          {(pageState === 'valid' || pageState === 'claiming') && invite && (
            <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm" data-testid="invite-form">
              <div className="text-center mb-6">
                <h1 className="text-xl font-semibold text-navy-900">
                  You've been invited
                </h1>
                <p className="mt-2 text-base font-medium text-navy-900">
                  {invite.regattaName}
                </p>
                <p className="text-sm text-muted-foreground">
                  {invite.boatName} ({formatSailNumber(invite.sailNumber ?? '')})
                </p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleClaim();
                }}
                className="space-y-4"
              >
                <div>
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    required
                    data-testid="display-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={invite.email || ''}
                    readOnly
                    className="bg-gray-50"
                    data-testid="email-input"
                  />
                </div>

                {/* Phone verification — gated by VITE_ENABLE_PHONE_VERIFY build flag.
                     Hides the step on environments where Twilio / Turnstile are not yet
                     provisioned. The backend REQUIRE_PHONE_VERIFY flag is independent. */}
                {import.meta.env.VITE_ENABLE_PHONE_VERIFY === 'true' && (
                  <div className="rounded-md border border-gray-200 p-4" data-testid="phone-verify-section">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Race alerts
                    </p>
                    {phoneVerify === null ? (
                      <PhoneVerificationStep
                        onVerified={(result) => setPhoneVerify(result)}
                        onSkip={() => setPhoneVerify({ notifyOptIn: false })}
                      />
                    ) : phoneVerify.verifyToken ? (
                      <PhoneVerificationStep
                        onVerified={(result) => setPhoneVerify(result)}
                        onSkip={() => setPhoneVerify({ notifyOptIn: false })}
                        existingVerifiedPhone="verified"
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Skipped — you can add a phone later in settings.
                      </p>
                    )}
                  </div>
                )}

                {errorMessage && pageState === 'valid' && (
                  <p className="text-sm text-destructive">{errorMessage}</p>
                )}

                <Button
                  type="submit"
                  className="w-full bg-brand-blue !text-navy-900 hover:bg-brand-dark"
                  disabled={pageState === 'claiming' || !displayName.trim()}
                  data-testid="claim-button"
                >
                  {pageState === 'claiming' ? 'Claiming...' : 'Claim My Boat'}
                </Button>
              </form>
            </div>
          )}
        </div>
    </div>
  );
}
